import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  Trash2,
  UploadCloud,
} from "lucide-react";

import api, { apiError, fileUrl } from "@/lib/api";
import { formatBytes, formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Note,
  SecureImage,
  inputClass,
} from "@/components/portal/Ui";

const TYPES = [
  { value: "", label: "All files" },
  { value: "image", label: "Images" },
  { value: "video", label: "Video" },
  { value: "document", label: "Documents" },
];

function kindIcon(contentType) {
  if ((contentType || "").startsWith("image/")) return ImageIcon;
  if ((contentType || "").startsWith("video/")) return Film;
  if ((contentType || "").includes("sheet") || (contentType || "").includes("csv"))
    return FileSpreadsheet;
  return FileText;
}

function MediaTile({ item, canDelete, onDelete }) {
  const Icon = kindIcon(item.content_type);
  const isImage = (item.content_type || "").startsWith("image/");
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#E6E1D8] bg-white"
      data-testid={`media-tile-${item.id}`}
    >
      <div className="relative flex h-40 items-center justify-center bg-[#F7F4EF]">
        {isImage ? (
          <SecureImage fileId={item.id} alt={item.original_filename} className="h-40 w-full object-cover" />
        ) : (
          <Icon className="h-10 w-10 text-[#b9b2a6]" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="truncate text-sm font-bold" title={item.original_filename}>
          {item.label || item.original_filename}
        </p>
        <p className="truncate text-xs body-text">{item.original_filename}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone="neutral">{formatBytes(item.size)}</Badge>
          {item.campaign_name && <Badge tone="blue">{item.campaign_name}</Badge>}
          {item.client_visible && <Badge tone="green">Client visible</Badge>}
        </div>
        <p className="mt-auto text-xs body-text">
          {item.uploaded_by_name} · {formatDateTime(item.created_at)}
        </p>
        <div className="flex items-center gap-2 pt-1">
          <a
            href={fileUrl(item.id)}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`media-open-${item.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F2811D] hover:underline"
          >
            <Download className="h-4 w-4" />
            Open
          </a>
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(item)}
              data-testid={`media-delete-${item.id}`}
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#B3261E] hover:underline"
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function MediaLibrary() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ label: "", campaign_id: "", client_visible: false });
  const fileInput = useRef(null);
  const [pending, setPending] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await api.get("/media", {
        params: { q: search || undefined, media_type: type || undefined, campaign_id: campaignFilter || undefined },
      });
      setData(response.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRefreshing(false);
    }
  }, [search, type, campaignFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/campaigns")
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  }, []);

  async function submitUpload(event) {
    event.preventDefault();
    if (!pending.length) {
      toast.error("Choose at least one file to upload.");
      return;
    }
    setBusy(true);
    let done = 0;
    const failed = [];
    try {
      for (const file of pending) {
        const body = new FormData();
        body.append("file", file);
        if (form.label && pending.length === 1) body.append("label", form.label);
        if (form.campaign_id) body.append("campaign_id", form.campaign_id);
        body.append("client_visible", form.client_visible ? "true" : "false");
        try {
          await api.post("/media", body, {
            onUploadProgress: (e) => {
              const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
              setProgress(Math.round(((done + pct / 100) / pending.length) * 100));
            },
          });
        } catch (e) {
          failed.push(`${file.name}: ${apiError(e)}`);
        }
        done += 1;
      }
      const uploaded = pending.length - failed.length;
      if (uploaded) toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} uploaded.`);
      failed.forEach((message) => toast.error(message));
      if (!failed.length) {
        setUploadOpen(false);
        setPending([]);
        setForm({ label: "", campaign_id: "", client_visible: false });
        if (fileInput.current) fileInput.current.value = "";
      }
      load();
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  async function removeFile() {
    const item = confirmDelete;
    setBusy(true);
    try {
      await api.delete(`/media/${item.id}`);
      toast.success("File removed from the library.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <ErrorNote message={error} onRetry={load} /> : <Loading />;

  const items = data.items || [];

  return (
    <div data-testid="media-library-page">
      <PageHeader
        title="Media Library"
        description="Every brand asset, deck and reference file kept in secure storage — searchable, shareable with clients, and tied to a campaign."
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <Button testId="media-upload-open" onClick={() => setUploadOpen(true)}>
            <UploadCloud className="h-4 w-4" />
            Upload files
          </Button>
        }
      />
      <ErrorNote message={error} onRetry={load} />

      <Card className="mb-5" testId="media-filters-card">
        <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr]">
          <input
            className={inputClass}
            placeholder="Search by file name or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="media-search-input"
          />
          <select
            className={inputClass}
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="media-type-filter"
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            className={inputClass}
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            data-testid="media-campaign-filter"
          >
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-3 text-xs body-text" data-testid="media-summary">
          {items.length} file{items.length === 1 ? "" : "s"} · {formatBytes(data.total_bytes)} stored
        </p>
      </Card>

      {items.length === 0 ? (
        <Empty
          testId="media-empty"
          title="Nothing in the library yet"
          body="Upload brand assets, campaign decks, product shots or raw creator files. Images and video preview in place; everything else downloads securely."
          action={
            <Button testId="media-upload-empty" onClick={() => setUploadOpen(true)}>
              <UploadCloud className="h-4 w-4" />
              Upload files
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="media-grid">
          {items.map((item) => (
            <MediaTile
              key={item.id}
              item={item}
              canDelete={user?.role === "admin" || item.uploaded_by === user?.id}
              onDelete={setConfirmDelete}
            />
          ))}
        </div>
      )}

      <Modal
        open={uploadOpen}
        onClose={() => !busy && setUploadOpen(false)}
        title="Upload to the media library"
        testId="media-upload-modal"
      >
        <form onSubmit={submitUpload} className="space-y-4">
          <Field label="Files" required hint="Images, video, PDF, CSV, XLSX, PPTX or DOCX. Up to 25 MB each.">
            <input
              ref={fileInput}
              type="file"
              multiple
              className={inputClass}
              onChange={(e) => setPending(Array.from(e.target.files || []))}
              data-testid="media-file-input"
            />
          </Field>
          {pending.length > 0 && (
            <Note testId="media-pending-note">
              {pending.length} file{pending.length === 1 ? "" : "s"} selected ·{" "}
              {formatBytes(pending.reduce((sum, f) => sum + f.size, 0))}
            </Note>
          )}
          {pending.length === 1 && (
            <Field label="Label" hint="Optional friendly name shown in the library.">
              <input
                className={inputClass}
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="media-label-input"
              />
            </Field>
          )}
          <Field label="Attach to campaign" hint="Optional. Needed if you want to share it with the client.">
            <select
              className={inputClass}
              value={form.campaign_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  campaign_id: e.target.value,
                  client_visible: e.target.value ? form.client_visible : false,
                })
              }
              data-testid="media-campaign-select"
            >
              <option value="">No campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={form.client_visible}
              disabled={!form.campaign_id}
              onChange={(e) => setForm({ ...form, client_visible: e.target.checked })}
              data-testid="media-client-visible-checkbox"
              className="mt-0.5 h-4 w-4 accent-[#F2811D]"
            />
            <span className="body-text">
              Let the campaign's client see and download these files
            </span>
          </label>
          {busy && (
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#F0ECE4]">
              <div
                className="h-full rounded-full bg-[#F2811D] transition-[width] duration-200"
                style={{ width: `${progress}%` }}
                data-testid="media-upload-progress"
              />
            </div>
          )}
          <div className="flex justify-end gap-2.5">
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={busy} testId="media-upload-cancel">
              Cancel
            </Button>
            <Button type="submit" busy={busy} testId="media-upload-submit">
              Upload
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!confirmDelete}
        onClose={() => !busy && setConfirmDelete(null)}
        title="Remove this file?"
        testId="media-delete-modal"
      >
        <p className="text-sm leading-relaxed body-text">
          {confirmDelete?.original_filename} will no longer be listed or downloadable. This cannot be
          undone from the app.
        </p>
        <div className="mt-6 flex justify-end gap-2.5">
          <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={busy} testId="media-delete-cancel">
            Keep it
          </Button>
          <Button variant="danger" busy={busy} onClick={removeFile} testId="media-delete-confirm">
            Remove file
          </Button>
        </div>
      </Modal>
    </div>
  );
}
